import React, { useEffect, useState } from "react";
import { Plug, CheckCircle2, XCircle, AlertCircle, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const CATEGORY_LABEL = { payments: "Payments", marketing: "Marketing", ads: "Advertising", email: "Email", sms: "SMS" };
const SECRET_KEYS = ["secret_key", "password", "api_key", "auth_token"];

function StatusBadge({ status }) {
  const map = {
    connected: { Icon: CheckCircle2, cls: "text-green-700 bg-green-100", label: "Connected" },
    configured: { Icon: AlertCircle, cls: "text-amber-700 bg-amber-100", label: "Configured (off)" },
    not_configured: { Icon: XCircle, cls: "text-muted-foreground bg-muted", label: "Not configured" },
  };
  const s = map[status] || map.not_configured;
  return <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold ${s.cls}`}><s.Icon size={13} /> {s.label}</span>;
}

export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [testing, setTesting] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);

  const load = () => api.get("/admin/integrations").then((r) => {
    setItems(r.data);
    const d = {}; r.data.forEach((i) => { d[i.key] = { ...i.config }; }); setDrafts(d);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setCfg = (key, field, value) => setDrafts((p) => ({ ...p, [key]: { ...p[key], [field]: value } }));

  const saveCfg = async (integ) => {
    await api.put(`/admin/integrations/${integ.key}`, { config: drafts[integ.key] });
    toast.success(`${integ.name} saved`); load();
  };
  const toggle = async (integ) => {
    await api.put(`/admin/integrations/${integ.key}`, { enabled: !integ.enabled, config: drafts[integ.key] });
    toast.success(`${integ.name} ${!integ.enabled ? "enabled" : "disabled"}`); load();
  };
  const test = async (integ) => {
    setTesting(integ.key);
    await api.put(`/admin/integrations/${integ.key}`, { config: drafts[integ.key] });
    try {
      const { data } = await api.post(`/admin/integrations/${integ.key}/test`);
      data.success ? toast.success(data.message) : toast.error(data.message);
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(null); load(); }
  };
  const viewInbox = async () => {
    setInboxLoading(true); setInbox([]);
    try { const { data } = await api.get("/admin/inbox"); setInbox(data.messages); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load inbox"); setInbox(null); }
    finally { setInboxLoading(false); }
  };

  const fieldLabel = (k) => k.replace(/_/g, " ");

  return (
    <div data-testid="admin-integrations">
      <h1 className="font-display text-3xl md:text-4xl font-medium text-primary mb-2">Integrations</h1>
      <p className="text-muted-foreground mb-8">Manage and monitor payments, marketing, ads and email connections.</p>

      <div className="space-y-5">
        {items.map((integ) => (
          <div key={integ.key} className="bg-card border border-primary/10 rounded-2xl p-6" data-testid={`integration-${integ.key}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Plug size={20} /></div>
                <div>
                  <div className="flex items-center gap-3"><h3 className="font-semibold text-primary text-lg">{integ.name}</h3><StatusBadge status={integ.status} /></div>
                  <p className="text-sm text-muted-foreground mt-0.5">{integ.description}</p>
                  <span className="text-[11px] uppercase tracking-wide text-secondary font-semibold">{CATEGORY_LABEL[integ.category]}</span>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-muted-foreground">Enabled</span>
                <button data-testid={`toggle-${integ.key}`} onClick={() => toggle(integ)} className={`w-11 h-6 rounded-full transition-colors relative ${integ.enabled ? "bg-secondary" : "bg-muted"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${integ.enabled ? "left-5" : "left-0.5"}`} />
                </button>
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-5">
              {Object.keys(drafts[integ.key] || {}).map((k) => {
                const val = drafts[integ.key][k];
                if (k === "response_template") return (
                  <div key={k} className="md:col-span-2">
                    <label className="text-xs text-muted-foreground">Custom response template</label>
                    <textarea rows={5} data-testid={`cfg-${integ.key}-${k}`} value={val} onChange={(e) => setCfg(integ.key, k, e.target.value)} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm font-mono" />
                    <p className="text-[11px] text-muted-foreground mt-1">Use {"{name}"} to personalise replies.</p>
                  </div>
                );
                if (typeof val === "boolean") return (
                  <label key={k} className="flex items-center gap-2 text-sm text-primary capitalize"><input type="checkbox" data-testid={`cfg-${integ.key}-${k}`} checked={val} onChange={(e) => setCfg(integ.key, k, e.target.checked)} className="w-4 h-4 accent-[hsl(var(--secondary))]" /> {fieldLabel(k)}</label>
                );
                return (
                  <div key={k}>
                    <label className="text-xs text-muted-foreground capitalize">{fieldLabel(k)}</label>
                    <input type={typeof val === "number" ? "number" : SECRET_KEYS.includes(k) ? "password" : "text"} data-testid={`cfg-${integ.key}-${k}`} value={val} onChange={(e) => setCfg(integ.key, k, typeof val === "number" ? Number(e.target.value) : e.target.value)} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" placeholder={SECRET_KEYS.includes(k) ? "••••••••" : ""} />
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-3 mt-5">
              <button onClick={() => saveCfg(integ)} data-testid={`save-${integ.key}`} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:bg-secondary transition-colors">Save config</button>
              <button onClick={() => test(integ)} disabled={testing === integ.key} data-testid={`test-${integ.key}`} className="rounded-full border border-primary/20 px-5 py-2 text-sm font-semibold text-primary hover:bg-primary/5 flex items-center gap-2">
                {testing === integ.key && <Loader2 size={14} className="animate-spin" />} Test connection
              </button>
              {integ.key === "imap_email" && (
                <button onClick={viewInbox} data-testid="view-inbox" className="rounded-full border border-secondary/40 text-secondary px-5 py-2 text-sm font-semibold hover:bg-secondary/10 flex items-center gap-2"><Inbox size={15} /> View inbox</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {inbox !== null && (
        <div className="fixed inset-0 z-[60] bg-primary/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setInbox(null)}>
          <div className="bg-background rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl font-semibold text-primary mb-4">IMAP Inbox</h2>
            {inboxLoading ? <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : inbox.length === 0 ? <p className="text-muted-foreground">No messages.</p> : (
              <div className="divide-y divide-primary/10">
                {inbox.map((m, i) => (
                  <div key={i} className="py-3">
                    <p className="font-semibold text-primary text-sm">{m.subject}</p>
                    <p className="text-xs text-muted-foreground">{m.from} · {m.date}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
